import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import moment from 'moment'
import { useContext, useMemo, useState } from 'react'
import { Calendar, EventProps, momentLocalizer, Navigate, SlotInfo, ToolbarProps, View } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useSearchParams } from 'react-router-dom'

import { Maybe, useFetchCourtDatesQuery, User, UserRole } from '../../../../generated'
import AttorneyDropdown from '../../../components/common/AttorneyDropdown'
import { ContentHead } from '../../../components/common/ContentHead'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS } from '../../../constants'
import { AuthContext } from '../../../context'
import palette from '../../../theme/palette'
import { CardBox } from '../../../theme/styleComponents'
import { infoText } from '../../../theme/styleConstants'
import { CourtDateDetailModal } from './CourtDateDetailModal'
import { CourtDateList, CourtDateListModal } from './CourtDateListModal'
import { CourtDateTodayChip } from './CourtDateTodayChip'
import {
  collapseBusySlots,
  COURT_DATE_TIMING_STYLES,
  COURT_DATES_VIEWS,
  CourtDateCalendarItem,
  CourtDateEvent,
  DATE_PARAM_FORMAT,
  getCourtDateTiming,
  getMatterNumber,
  getMatterStatusStyle,
  getVisibleRange,
  isCourtDatesView,
  isSlotGroup,
  MATTER_STATUS_STYLES,
  PAST_COURT_DATE_OPACITY,
  getWeekDisplayEnd,
  SLOT_MINUTES,
  toCalendarEvent,
  VISIBLE_PER_SLOT
} from './courtDatesUtils'

const localizer = momentLocalizer(moment)

const TITLE_LINE_HEIGHT = 14
const CLIENT_LINE_HEIGHT = 14

/**
 * The month view decides how many court dates fit in a day cell by measuring a hidden one-line dummy
 * `.rbc-event` with no border. Ours are two lines and bordered, so the cell over-filled and pushed
 * "+N more" past its bottom edge. Giving every month-view event, the dummy included, the same fixed
 * two-line height and a 1px border keeps that measurement true at any screen height. The month view
 * also keeps a whole card's height free for "+N more", so the cards are kept compact (14px lines,
 * 1px vertical padding) to fit one more court date per day.
 */
const monthEventSizing = {
  '& .rbc-month-view .rbc-event': { border: '1px solid transparent', paddingTop: '1px', paddingBottom: '1px' },
  '& .rbc-month-view .rbc-event-content': { height: `${TITLE_LINE_HEIGHT + CLIENT_LINE_HEIGHT}px`, overflow: 'hidden' }
}

const CourtDateEventLabel = ({
  event: item,
  showStartTime
}: EventProps<CourtDateCalendarItem> & { showStartTime?: boolean }) => {
  if (isSlotGroup(item)) {
    return (
      <Typography fontSize="12px" fontWeight={600} color="primary" noWrap>
        {item.title}
      </Typography>
    )
  }

  const event = item
  const matterNumber = getMatterNumber(event.courtDate)
  const { Icon } = getMatterStatusStyle(event.courtDate.caseStatus)
  const timing = getCourtDateTiming(event)
  const { color, filled } = COURT_DATE_TIMING_STYLES[timing]

  return (
    <Box display="flex" alignItems="flex-start" gap={0.5} overflow="hidden">
      <Icon sx={{ fontSize: '14px', color: filled ? 'white' : color, mt: '1px', flexShrink: 0 }} />
      <Box minWidth={0} flex={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            fontSize="12px"
            fontWeight={500}
            lineHeight={`${TITLE_LINE_HEIGHT}px`}
            color={filled ? 'white' : 'secondary.main'}
            noWrap
            flex={1}
          >
            {showStartTime && `${moment(event.start).format('h:mm A')} · `}
            {matterNumber ? `${matterNumber} · ${event.title}` : event.title}
          </Typography>
          {/* Not in the week view: its columns are too narrow, and today's column is already highlighted */}
          {timing === 'today' && !showStartTime && <CourtDateTodayChip onFilled={filled} />}
        </Box>
        {event.courtDate.clientName && (
          <Typography
            fontSize="11px"
            lineHeight={`${CLIENT_LINE_HEIGHT}px`}
            color={filled ? 'rgba(255, 255, 255, 0.85)' : 'lightGrayOne'}
            noWrap
          >
            {event.courtDate.clientName}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const CourtDatesLegend = () => (
  <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="center" columnGap={3} rowGap={1}>
    {[COURT_DATE_TIMING_STYLES.upcoming, COURT_DATE_TIMING_STYLES.past].map(({ label, color, filled }) => (
      <Box key={label} display="flex" alignItems="center">
        <Box
          sx={{
            ...infoText,
            border: `2px solid ${color}`,
            backgroundColor: filled ? color : 'transparent',
            opacity: filled ? 1 : PAST_COURT_DATE_OPACITY
          }}
        />
        <Typography fontSize="13px">{label}</Typography>
      </Box>
    ))}

    {Object.values(MATTER_STATUS_STYLES).map(({ label, Icon }) => (
      <Box key={label} display="flex" alignItems="center" gap={0.5}>
        <Icon sx={{ fontSize: '16px', color: 'lightGrayFive' }} />
        <Typography fontSize="13px">{label} matter</Typography>
      </Box>
    ))}
  </Box>
)

/**
 * Replaces the library's plain-button toolbar with the portal's MUI controls: views on the left,
 * Today / previous / label / next in the middle, legend on the right. Stacks on small screens.
 */
const CourtDatesToolbar = ({ label, view, onNavigate, onView }: ToolbarProps<CourtDateCalendarItem>) => (
  <Box display="grid" gridTemplateColumns={{ xs: '1fr', lg: '1fr auto 1fr' }} alignItems="center" gap={2} mb={2}>
    <ToggleButtonGroup
      exclusive
      size="small"
      value={view}
      onChange={(_event, newView: View | null) => newView && onView(newView)}
      aria-label="calendar view"
      sx={{ justifySelf: { xs: 'center', lg: 'start' } }}
    >
      {COURT_DATES_VIEWS.map(calendarView => (
        <ToggleButton key={calendarView} value={calendarView} sx={{ px: 2, textTransform: 'capitalize' }}>
          {calendarView}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>

    {/* Below md (767px in this theme) Today drops to its own line under the arrows; inline it overflowed */}
    <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="center" columnGap={1} rowGap={0.5}>
      <Box order={{ xs: 1, md: 0 }} flexBasis={{ xs: '100%', md: 'auto' }} display="flex" justifyContent="center">
        <Button variant="outlined" size="small" onClick={() => onNavigate(Navigate.TODAY)}>
          Today
        </Button>
      </Box>
      {/* Arrows and label never wrap apart; the label gives way instead */}
      <Box display="flex" alignItems="center" flexWrap="nowrap" minWidth={0} maxWidth="100%">
        <IconButton aria-label="previous" onClick={() => onNavigate(Navigate.PREVIOUS)}>
          <ChevronLeft />
        </IconButton>
        <Typography
          fontSize={{ xs: '16px', md: '18px' }}
          fontWeight={500}
          textAlign="center"
          noWrap
          minWidth={{ xs: 0, md: '200px' }}
        >
          {label}
        </Typography>
        <IconButton aria-label="next" onClick={() => onNavigate(Navigate.NEXT)}>
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>

    <Box justifySelf={{ xs: 'center', lg: 'end' }}>
      <CourtDatesLegend />
    </Box>
  </Box>
)

/** Days before today get a light grey background, so past dates read as past at a glance. */
const pastDayStyle = (day: Date) =>
  moment(day).isBefore(moment(), 'day') ? { style: { backgroundColor: '#F9FAFB' } } : {}

/**
 * Court dates across matters (HCK-262, HCK-270). Admins and super-admins see the whole firm and can narrow
 * to one Resp 1 attorney; an attorney sees only their own Resp 1 matters and a paralegal only the matters
 * they are assigned to or created (both, for someone who is both). The API enforces that; the
 * missing filter here is only the matching UI. View, day and attorney live in the URL so going back
 * from a matter returns to the same place.
 */
const CourtDates = () => {
  const { isAdmin, isSuperAdmin } = useContext(AuthContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedEvent, setSelectedEvent] = useState<CourtDateEvent | null>(null)
  const [moreList, setMoreList] = useState<CourtDateList | null>(null)
  // Phones and small tablets open on the day view, which is the only one readable there
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  // Accountants have no access; an attorney or paralegal who is also an admin keeps the firm-wide view, as on the API
  const canFilterByAttorney = !!(isAdmin || isSuperAdmin)

  const viewParam = searchParams.get(QUERY_PARAMS.CALENDAR_VIEW)
  const view = isCourtDatesView(viewParam) ? viewParam : isSmallScreen ? 'day' : 'month'
  const dateParam = moment(searchParams.get(QUERY_PARAMS.CALENDAR_DATE) || '', DATE_PARAM_FORMAT, true)
  const date = dateParam.isValid() ? dateParam.toDate() : new Date()
  const attorneyId = canFilterByAttorney ? searchParams.get(QUERY_PARAMS.USER) || undefined : undefined
  const { startDate, endDate } = getVisibleRange(date, view)

  const updateParams = (updates: Partial<Record<QUERY_PARAMS, string | undefined>>) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)))
    setSearchParams(next, { replace: true })
  }

  const { data, loading, error } = useFetchCourtDatesQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { courtDatesInput: { startDate, endDate, responsibleAttorneyId: attorneyId } },
    onError() {}
  })

  const events = useMemo(
    () => (data?.fetchCourtDates?.courtDates || []).map(toCalendarEvent),
    [data?.fetchCourtDates?.courtDates]
  )

  // Week and day views cap each half-hour slot; the month view already caps each day itself
  const calendarItems = useMemo<CourtDateCalendarItem[]>(
    () =>
      view === 'month'
        ? events
        : // A phone-width day column only has room for one court date per slot beside its "View N more"
          collapseBusySlots(events, isSmallScreen ? 1 : VISIBLE_PER_SLOT[view], SLOT_MINUTES[view], count =>
            view === 'day' ? `View ${count} more` : `+${count} more`
          ),
    [events, view, isSmallScreen]
  )

  const handleSelectItem = (item: CourtDateCalendarItem) =>
    isSlotGroup(item)
      ? setMoreList({
          title: `${moment(item.start).format('ddd, MMM D · h:mm A')} · ${item.slotCourtDates.length} court dates`,
          events: item.slotCourtDates
        })
      : setSelectedEvent(item)

  // Replaces the library's popup, which opened under the page header and could not scroll
  const handleShowMore = (dayItems: CourtDateCalendarItem[], day: Date) => {
    const dayEvents = dayItems.filter((item): item is CourtDateEvent => !isSlotGroup(item))
    setMoreList({ title: `${moment(day).format('dddd, MMM D')} · ${dayEvents.length} court dates`, events: dayEvents })
  }

  const handleNavigate = (newDate: Date) =>
    updateParams({ [QUERY_PARAMS.CALENDAR_DATE]: moment(newDate).format(DATE_PARAM_FORMAT) })

  const handleView = (newView: View) =>
    isCourtDatesView(newView) && updateParams({ [QUERY_PARAMS.CALENDAR_VIEW]: newView })

  // Picking a day in the month or week grid opens that day's court dates
  const handleSelectSlot = ({ start }: SlotInfo) =>
    view !== 'day' &&
    updateParams({
      [QUERY_PARAMS.CALENDAR_VIEW]: 'day',
      [QUERY_PARAMS.CALENDAR_DATE]: moment(start).format(DATE_PARAM_FORMAT)
    })

  const handleDrillDown = (drillDate: Date) =>
    updateParams({
      [QUERY_PARAMS.CALENDAR_VIEW]: 'day',
      [QUERY_PARAMS.CALENDAR_DATE]: moment(drillDate).format(DATE_PARAM_FORMAT)
    })

  return (
    <Box>
      <ContentHead
        title="Court Dates"
        customButton={() =>
          canFilterByAttorney ? (
            <AttorneyDropdown
              label="Resp 1 Attorney"
              placeholder="All attorneys"
              size="small"
              roles={[UserRole.Attorney]}
              selectedAttorneyId={attorneyId}
              onChange={(value: Maybe<User>) => updateParams({ [QUERY_PARAMS.USER]: value?.id })}
              sx={{ minWidth: 260 }}
            />
          ) : null
        }
      />

      <CardBox mt={2} mb={3}>
        <Typography fontSize="14px" color="lightGrayOne" px={3} py={2}>
          {loading ? 'Loading court dates…' : `${events.length} court date${events.length === 1 ? '' : 's'}`}
        </Typography>

        {error && (
          <Typography color="error" fontSize="14px" px={3} pb={2}>
            Court dates could not be loaded. Please try again.
          </Typography>
        )}

        <Box height="4px">{loading && <LinearProgress />}</Box>

        <Box
          px={{ xs: 1, sm: 3 }}
          pb={3}
          overflow="auto"
          // react-big-calendar's own blue (today, "+N more") replaced with the portal's colours
          sx={{
            ...monthEventSizing,
            '& .rbc-today': { backgroundColor: 'rgba(119, 17, 23, 0.04)' },
            '& .rbc-now .rbc-button-link': { color: palette.primary.main, fontWeight: 600 },
            '& .rbc-show-more': { color: palette.primary.main, fontWeight: 500, backgroundColor: 'transparent' },
            // "+N more" blocks: the time range the library prints above every event is noise there
            '& .court-dates-more .rbc-event-label': { display: 'none' },
            // The week view draws court dates cut to their hour, so the card shows the real start time instead
            ...(view === 'week' && { '& .rbc-event-label': { display: 'none' } })
          }}
        >
          <Box minWidth={view === 'day' ? undefined : '700px'}>
            <Calendar<CourtDateCalendarItem>
              selectable
              popup={false}
              doShowMoreDrillDown={false}
              onShowMore={handleShowMore}
              dayLayoutAlgorithm="no-overlap"
              localizer={localizer}
              events={calendarItems}
              views={COURT_DATES_VIEWS}
              view={view}
              date={date}
              onView={handleView}
              onNavigate={handleNavigate}
              onDrillDown={handleDrillDown}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectItem}
              scrollToTime={moment().startOf('day').hour(8).toDate()}
              tooltipAccessor={event => {
                if (isSlotGroup(event))
                  return `${event.slotCourtDates.length} court dates at ${moment(event.start).format('h:mm A')}`
                const matterNumber = getMatterNumber(event.courtDate)
                return [matterNumber && `Matter ${matterNumber}`, event.title, event.courtDate.clientName]
                  .filter(Boolean)
                  .join(' · ')
              }}
              // Today's and upcoming court dates are filled, past ones outlined and faded; the icon is the matter status
              eventPropGetter={(event, _start, _end, isSelected) => {
                if (isSlotGroup(event)) {
                  return {
                    className: 'court-dates-more',
                    style: {
                      backgroundColor: isSelected ? palette.lightGrayTwo : 'white',
                      border: `1px dashed ${palette.primary.main}`,
                      borderRadius: '4px'
                    }
                  }
                }

                const { color, filled } = COURT_DATE_TIMING_STYLES[getCourtDateTiming(event)]

                return {
                  style: filled
                    ? {
                        backgroundColor: color,
                        border: `1px solid ${color}`,
                        borderRadius: '4px',
                        color: 'white',
                        filter: isSelected ? 'brightness(0.85)' : undefined
                      }
                    : {
                        backgroundColor: isSelected ? palette.lightGrayTwo : 'white',
                        border: `1px solid ${color}`,
                        borderRadius: '4px',
                        color: palette.secondary.main,
                        opacity: PAST_COURT_DATE_OPACITY
                      }
                }
              }}
              dayPropGetter={pastDayStyle}
              endAccessor={item => (view === 'week' && !isSlotGroup(item) ? getWeekDisplayEnd(item) : item.end)}
              components={{
                event: props => <CourtDateEventLabel {...props} showStartTime={view === 'week'} />,
                toolbar: CourtDatesToolbar
              }}
              style={{ height: 'max(640px, calc(100vh - 300px))' }}
            />
          </Box>
        </Box>
      </CardBox>

      <CourtDateListModal list={moreList} onSelect={setSelectedEvent} onClose={() => setMoreList(null)} />
      <CourtDateDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </Box>
  )
}

export default CourtDates
